import React, { useEffect, useMemo, useRef, useState } from 'react';
import type {
    ElasticSearchFilters,
    PCAReportMetric,
    Campaign,
    PCAMapReportPlayStatsMinimal,
} from 'c-sdk';
import { EsPCAReportFilterField, PCAReportCountField } from 'c-sdk';
import { Box, CardHeader, Grid, Typography } from '@mui/material';
import { ReportDateTimeframe } from 'c-reports/Types';
import type { ReportTabProps } from 'c-main/Components/Campaign/CampaignReports/types';
import { FieldReportType, ReportView } from 'c-main/Components/Campaign/CampaignReports/types';
import { getCampaignReportEndDate, getCampaignReportStartDate } from 'c-main/Lib';
import { LocationOn, Tv } from '@mui/icons-material';
import { Translate } from 'c-translation';
import { MuiRouterLink } from 'c-components';
import ByFieldES from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/ByFieldES';
import { googleMapsLatLngUrl } from 'c-lib';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import MetricPerformance from '../../MetricPerformance';

type Props = {
    frameId: string;
    frameInfo: PCAMapReportPlayStatsMinimal;
    metric: PCAReportMetric;
    campaign: Campaign;
    start: Date;
    end: Date;
    accumulativeOverTime: boolean;
    setAccumulativeOverTime: (state: boolean) => void;
    environment: string;
    fieldFilters?: ElasticSearchFilters;
    showVariance: boolean;
};

const truncateToTwoDecimals = (num: number): number => Math.floor(num * 100) / 100;
const onFiltersUpdated = () => {};
const percentageCalc = (actual: number, data: any, metric: string) => {
    if (metric === 'plays') {
        return actual <= 0
            ? 0
            : truncateToTwoDecimals((actual / data?.expected_plays_in_schedule) * 100);
    }
    if (metric === 'time') {
        return actual <= 0
            ? 0
            : truncateToTwoDecimals((actual / data?.expected_time_in_schedule) * 100);
    }
    return actual <= 0
        ? 0
        : truncateToTwoDecimals((actual / data?.expected_impacts_in_schedule) * 100);
};

const SelectedFrameInfo: React.FC<Props> = ({
    frameId,
    frameInfo,
    campaign,
    metric,
    start,
    end,
    accumulativeOverTime,
    setAccumulativeOverTime,
    environment: PCADataEnvironment,
    showVariance,
}) => {
    const wrapperRef = useRef<HTMLDivElement>();

    const { start: getDisplayData, data } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.displayFrameIdMapData,
    );
    useEffect(() => {
        getDisplayData(campaign.id, frameInfo[frameInfo.length - 1]);
    }, [campaign.id, frameInfo, getDisplayData]);

    const campaignStart = useMemo(
        () => getCampaignReportStartDate(campaign, PCADataEnvironment) ?? new Date(),
        [campaign, PCADataEnvironment],
    );
    const campaignEnd = useMemo(
        () => getCampaignReportEndDate(campaign, PCADataEnvironment) ?? new Date(),
        [campaign, PCADataEnvironment],
    );

    const esFieldFilters = useMemo<ElasticSearchFilters>(
        () => ({ [EsPCAReportFilterField.FrameId]: [frameId] }),
        [frameId],
    );

    const expected = useMemo(
        () =>
            metric === 'plays'
                ? (data?.data as any)?.data?.[frameId]?.expected_plays_in_schedule
                : metric === 'time'
                ? (data?.data as any)?.data?.[frameId]?.expected_time_in_schedule
                : (data?.data as any)?.data?.[frameId]?.expected_impacts_in_schedule,
        [data?.data, frameId, metric],
    );
    const actual = useMemo(() => frameInfo[0], [frameInfo]);

    const percentage = useMemo(
        () => percentageCalc(actual, (data?.data as any)?.data?.[frameId], metric),
        [actual, data?.data, frameId, metric],
    );

    const inSchedule = useMemo(() => {
        const frameData = (data?.data as any)?.data?.[frameId];
        if (!frameData) return undefined;

        return metric === 'plays'
            ? frameData.plays_received_in_schedule
            : metric === 'time'
            ? frameData.time_received_in_schedule
            : frameData.impacts_received_in_schedule;
    }, [data?.data, frameId, metric]);

    const outSchedule = useMemo(() => {
        const frameData = (data?.data as any)?.data?.[frameId];
        if (!frameData) return undefined;

        return metric === 'plays'
            ? frameData.plays_received_out_of_schedule
            : metric === 'time'
            ? frameData.time_received_out_of_schedule
            : frameData.impacts_received_out_of_schedule;
    }, [data?.data, frameId, metric]);

    const unbooked = useMemo(() => {
        const frameData = (data?.data as any)?.data?.[frameId];
        if (!frameData) return undefined;

        return metric === 'plays'
            ? frameData.plays_received_unbooked
            : metric === 'time'
            ? frameData.time_received_unbooked
            : frameData.impacts_received_unbooked;
    }, [data?.data, frameId, metric]);

    const latitude = useMemo(() => frameInfo[6], [frameInfo]);
    const longitude = useMemo(() => frameInfo[7], [frameInfo]);

    const town = useMemo(
        () => (data?.data as any)?.data?.[frameId]?.display_town,
        [data?.data, frameId],
    );
    const environment = useMemo(
        () => (data?.data as any)?.data?.[frameId]?.display_env,
        [data?.data, frameId],
    );
    const screenFormat = useMemo(
        () => (data?.data as any)?.data?.[frameId]?.display_format,
        [data?.data, frameId],
    );
    const mediaOwner = useMemo(
        () => (data?.data as any)?.data?.[frameId]?.display_owner,
        [data?.data, frameId],
    );
    const resolution = useMemo(
        () => (data?.data as any)?.data?.[frameId]?.display_resolution,
        [data?.data, frameId],
    );

    const [selectedFrameTimeframe, setSelectedFrameTimeframe] = useState(ReportDateTimeframe.All);

    const reportTabProps = useMemo<ReportTabProps>(
        () => ({
            campaign,
            campaignStart,
            campaignEnd,
            currentStartDate: start,
            currentEndDate: end,
            metric,
            overTimeAccumulative: accumulativeOverTime,
            setOverTimeAccumulative: setAccumulativeOverTime,
            dateSettings: {
                onDatesUpdated: newTimeFrame => setSelectedFrameTimeframe(newTimeFrame),
                timeframe: ReportDateTimeframe.All,
                availableTimeframes: [
                    ReportDateTimeframe.Daily,
                    ReportDateTimeframe.All,
                    ReportDateTimeframe.Range,
                    ReportDateTimeframe.Monthly,
                    ReportDateTimeframe.Weekly,
                ],
            },
            countFields: [
                PCAReportCountField.UnBooked,
                PCAReportCountField.OutSchedule,
                PCAReportCountField.InSchedule,
            ],

            environment: PCADataEnvironment,
        }),
        [
            campaign,
            campaignStart,
            campaignEnd,
            start,
            end,
            metric,
            accumulativeOverTime,
            setAccumulativeOverTime,
            PCADataEnvironment,
        ],
    );

    useEffect(() => {
        if (wrapperRef.current) {
            wrapperRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [frameId]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} component="div" ref={wrapperRef}>
                <CardHeader
                    sx={{ pb: 0 }}
                    title={
                        <>
                            <Tv />{' '}
                            <Box ml={1}>
                                {frameInfo[11]} ({frameId})
                            </Box>
                        </>
                    }
                    titleTypographyProps={{ sx: { mt: 2, display: 'flex', alignItems: 'center' } }}
                    subheader={
                        <>
                            <LocationOn />
                            <MuiRouterLink
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                component="a"
                                href={googleMapsLatLngUrl(latitude, longitude)}
                                target="_blank"
                                sx={{ display: 'flex', color: 'inherit' }}
                            >
                                {town}
                                <Box ml={1}>
                                    {latitude}, {longitude}
                                </Box>
                            </MuiRouterLink>
                        </>
                    }
                    subheaderTypographyProps={{
                        sx: { mt: 2, display: 'flex', alignItems: 'center' },
                    }}
                />
            </Grid>
            <Grid item xs={12}>
                <Grid container spacing={2} sx={{ pl: 2 }}>
                    <Block
                        title={
                            <Translate path="Modules.Main.Campaigns.Overview.Reports.fields.mediaOwner" />
                        }
                        value={mediaOwner}
                    />
                    <Block
                        title={
                            <Translate path="Modules.Main.Campaigns.Overview.Reports.fields.environment" />
                        }
                        value={environment}
                    />
                    <Block
                        title={
                            <Translate path="Modules.Main.Campaigns.Overview.Reports.fields.resolution" />
                        }
                        value={resolution}
                    />
                    <Block
                        title={
                            <Translate path="Modules.Main.Campaigns.Overview.Reports.fields.format" />
                        }
                        value={screenFormat}
                    />
                </Grid>
            </Grid>
            <Box width="100%" my={3}>
                <MetricPerformance
                    metric={metric}
                    performance={percentage}
                    actual={actual}
                    expected={expected}
                    inSchedule={inSchedule}
                    outSchedule={outSchedule}
                    unbooked={unbooked}
                    showVariance={showVariance}
                />
            </Box>
            <Grid item xs={12}>
                <ByFieldES
                    field={null}
                    view={ReportView.Field}
                    fieldReportType={FieldReportType.OverTime}
                    initialTimeFrame={selectedFrameTimeframe}
                    {...reportTabProps}
                    currentStartDate={start}
                    currentEndDate={end}
                    fieldFilters={esFieldFilters}
                    refineFilters={false}
                    onFiltersUpdated={onFiltersUpdated}
                    onResponse={onFiltersUpdated}
                    groupResultsByDate
                />
            </Grid>
        </Grid>
    );
};

const Block: React.FC<{ title: React.ReactNode; value: React.ReactNode }> = ({ title, value }) => (
    <Grid item xs={12} sm={6} md="auto">
        <Typography color="grey.500">{title}</Typography>
        <Typography sx={{ maxWidth: '20em', wordBreak: 'break-word' }}>{value}</Typography>
    </Grid>
);

export default SelectedFrameInfo;
